import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, ArrowLeft, ArrowRight, ShoppingBag, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export default function Cart() {
  const { items, removeFromCart, getTotal, clearCart } = useCart();
  const { isAuthenticated, token } = useAuth();
  const [guestEmail, setGuestEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [hasBundle, setHasBundle] = useState(false);
  const navigate = useNavigate();

  // Check for bundle discount
  useEffect(() => {
    const checkBundle = () => {
      const productIds = items.map(item => item.id);
      const hasBoth = productIds.includes('signal-full-guide') && productIds.includes('signal-accountability');
      setHasBundle(hasBoth);
      setDiscount(hasBoth ? 5.00 : 0);
    };
    checkBundle();
  }, [items]);

  const subtotal = getTotal();
  const total = subtotal - discount;

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!isAuthenticated && !guestEmail) {
      toast.error('Please enter your email for order confirmation');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      
      let response;
      if (isAuthenticated) {
        response = await axios.post(`${API}/checkout/create-session`, 
          { origin_url: originUrl },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(`${API}/checkout/create-session-guest`, {
          items: items.map(item => ({ product_id: item.id, quantity: item.quantity })),
          origin_url: originUrl,
          email: guestEmail
        });
      }

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="pt-28 bg-black min-h-screen" data-testid="empty-cart">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <ShoppingBag className="w-16 h-16 text-[#E5E0D6] mx-auto mb-6" />
            </motion.div>
            <motion.h1 variants={fadeInUp} className="font-serif text-3xl md:text-4xl text-white mb-4">
              Your Cart is Empty
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-[#999999] mb-8">
              Looks like you haven't added anything to your cart yet.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/products">
                <Button 
                  className="btn-premium bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none px-8 py-4 text-sm uppercase tracking-widest"
                  data-testid="shop-now-button"
                >
                  Browse Products
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="cart-page">
      {/* Header */}
      <section className="py-12 md:py-16 border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/products" 
            className="inline-flex items-center text-sm text-[#999999] hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Continue Shopping
          </Link>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
            Shopping Cart
          </h1>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-3 gap-12"
          >
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {items.map((item) => (
                <motion.div 
                  key={item.id}
                  variants={fadeInUp}
                  className="flex gap-6 p-6 bg-[#0f0f0f] border border-[#1a1a1a]"
                  data-testid={`cart-item-${item.id}`}
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-[#0f0f0f] flex items-center justify-center flex-shrink-0">
                    {item.id === 'signal-accountability' ? (
                      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-1">
                        {item.type === 'subscription' ? 'Monthly' : 'One-Time'}
                      </p>
                      <h3 className="font-serif text-xl text-white mb-2">{item.name}</h3>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <span className="font-serif text-xl text-white">
                        €{item.price.toFixed(2)}
                        {item.type === 'subscription' && <span className="text-sm text-[#999999]">/mo</span>}
                      </span>
                      <button
                        onClick={() => {
                          removeFromCart(item.id);
                          toast.success('Removed from cart');
                        }}
                        className="p-2 text-[#999999] hover:text-red-600 transition-colors"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Bundle Suggestion */}
              {!hasBundle && items.length === 1 && (
                <motion.div 
                  variants={fadeInUp}
                  className="p-6 bg-[#0f0f0f] border border-[#D4AF37]"
                >
                  <div className="flex items-start gap-4">
                    <Tag className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-white mb-1">Save €10 with the Bundle</h4>
                      <p className="text-sm text-[#999999] mb-3">
                        Add {items[0].id === 'signal-full-guide' ? 'Prime Accountability' : 'The Signal Guide'} and save €10 on your order.
                      </p>
                      <Link to="/products">
                        <Button 
                          variant="outline"
                          size="sm"
                          className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white rounded-none text-xs"
                        >
                          View Products
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Order Summary */}
            <motion.div variants={fadeInUp} className="lg:col-span-1">
              <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-6 md:p-8 sticky top-28">
                <h2 className="font-serif text-2xl text-white mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6 pb-6 border-b border-[#1a1a1a]">
                  {items.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-[#999999]">{item.name}</span>
                      <span className="text-white">€{item.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                {/* Subtotal */}
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#999999]">Subtotal</span>
                  <span className="text-white">€{subtotal.toFixed(2)}</span>
                </div>

                {/* Bundle Discount */}
                {hasBundle && (
                  <div className="flex justify-between text-sm mb-4 text-green-600">
                    <span className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Bundle Discount
                    </span>
                    <span>-€{discount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between mb-8 pt-4 border-t border-[#1a1a1a]">
                  <span className="font-semibold text-white">Total</span>
                  <span className="font-serif text-2xl text-white" data-testid="cart-total">
                    €{total.toFixed(2)}
                  </span>
                </div>

                {/* Guest Email */}
                {!isAuthenticated && (
                  <div className="mb-6">
                    <label className="block text-sm text-[#999999] mb-2">
                      Email for order confirmation
                    </label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      className="rounded-none border-[#1a1a1a] focus:border-[#1A1A1A]"
                      data-testid="guest-email-input"
                    />
                  </div>
                )}

                <Button 
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full btn-premium bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none py-6 text-sm uppercase tracking-widest font-semibold"
                  data-testid="checkout-button"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <span className="spinner mr-2" />
                      Processing...
                    </span>
                  ) : (
                    <>
                      Proceed to Checkout
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>

                {!isAuthenticated && (
                  <p className="text-center text-sm text-[#999999] mt-4">
                    <Link to="/login" className="text-[#D4AF37] hover:underline">
                      Login
                    </Link>
                    {' '}for faster checkout
                  </p>
                )}

                <p className="text-xs text-[#999999] mt-6 text-center">
                  Secure payment powered by Stripe
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
